/** @format */

import { type ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CloseIcon, CrossIcon } from 'src/components/icons';
import { flattenTopicTreeForAdmin } from 'src/utils/administration';
import type { Topic } from 'src/types';

function buildTopicParentMap(nodes) {
  const parentByChildId = new Map();
  const walk = (topics, parentId = null) => {
    if (!Array.isArray(topics)) return;
    topics.forEach(topic => {
      if (topic?.id == null) return;
      const idStr = String(topic.id);
      if (parentId != null) {
        parentByChildId.set(idStr, String(parentId));
      }
      if (Array.isArray(topic.children) && topic.children.length > 0) {
        walk(topic.children, topic.id);
      }
    });
  };
  walk(nodes, null);
  return parentByChildId;
}

function buildTopicNodeMap(nodes) {
  const map = new Map();
  const walk = topics => {
    if (!Array.isArray(topics)) return;
    topics.forEach(topic => {
      if (topic?.id == null) return;
      map.set(String(topic.id), topic);
      if (topic.children?.length) walk(topic.children);
    });
  };
  walk(nodes);
  return map;
}

function collectDescendantIds(topic) {
  if (!topic || !Array.isArray(topic.children) || topic.children.length === 0) return [];
  return topic.children.reduce((acc, childTopic) => {
    acc.push(String(childTopic.id));
    return [...acc, ...collectDescendantIds(childTopic)];
  }, []);
}

function isCompleteSubtreeSelected(rootId, selectedSet, topicMap) {
  const key = String(rootId);
  if (!selectedSet.has(key)) return false;
  const node = topicMap.get(key);
  if (!node) return false;
  const desc = collectDescendantIds(node);
  if (!desc.length) return true;
  return desc.every(d => selectedSet.has(String(d)));
}

function hasIncompleteSelectedAncestor(topicIdStr, parentByChildId, selectedSet, topicMap) {
  let cur = topicIdStr;
  while (parentByChildId.has(cur)) {
    const parentId = parentByChildId.get(cur);
    if (selectedSet.has(parentId) && !isCompleteSubtreeSelected(parentId, selectedSet, topicMap)) {
      return true;
    }
    cur = parentId;
  }
  return false;
}

/** Show all chips when a selected ancestor has its full subtree selected; hide descendants only under a partial selection. */
function filterTopicIdsForChipRow(selectedIds, parentByChildId, topicMap) {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  if (!ids.length) return [];
  const selectedSet = new Set(ids.map(id => String(id)));
  const seen = new Set();
  return ids.filter(topicId => {
    const key = String(topicId);
    if (seen.has(key)) return false;
    if (hasIncompleteSelectedAncestor(key, parentByChildId, selectedSet, topicMap)) return false;
    seen.add(key);
    return true;
  });
}

type TopicSearchVariant = 'newsletter' | 'contentEditor';

interface TopicSearchVariantStyles {
  list: string;
  item: string;
  button: string;
  buttonSelected: string;
  label: string;
  meta: string;
  chipRemoveClass: string;
  itemLoading?: string;
  loadingText?: string;
  fieldExtra?: string;
  outer?: string;
}

const VARIANT_STYLES: Record<TopicSearchVariant, TopicSearchVariantStyles> = {
  newsletter: {
    list: 'personal-data__newsletter-autocomplete-list',
    item: 'personal-data__newsletter-autocomplete-item',
    button: 'personal-data__newsletter-autocomplete-option',
    buttonSelected: 'personal-data__newsletter-autocomplete-option--selected',
    label: 'personal-data__newsletter-autocomplete-option-label',
    meta: 'personal-data__newsletter-autocomplete-option-selected',
    chipRemoveClass: 'keyword-search__keyword-remove'
  },
  contentEditor: {
    list: 'content-editor__tag-suggestions',
    item: 'content-editor__tag-suggestion-item',
    itemLoading: 'content-editor__tag-suggestion-item--loading',
    loadingText: 'content-editor__tag-search-loading',
    button: 'content-editor__tag-suggestion-btn',
    buttonSelected: 'content-editor__tag-suggestion-btn--selected',
    label: 'content-editor__tag-suggestion-label',
    meta: 'content-editor__tag-suggestion-meta',
    chipRemoveClass: 'keyword-search__keyword-remove content-editor__tag-keyword-remove',
    fieldExtra: 'content-editor__topic-search-field content-editor__tag-search-field',
    outer: 'content-editor__topic-search'
  }
};

interface TopicSearchAutocompleteProps {
  variant: TopicSearchVariant;
  idPrefix: string;
  topicTree?: Topic[];
  selectedTopicIds?: Array<string | number>;
  onToggleTopic: (topicId: string | number) => void;
  onAddTopicFromSearch?: (topicId: string | number) => void;
  loading?: boolean;
  error?: string | Record<string, unknown> | null;
  loadingMessage?: string;
  placeholder: string;
  inputAriaLabel: string;
  listAriaLabel: string;
  selectedBadgeText: string;
  getRemoveChipAriaLabel: (label: string) => string;
  trailingSlot?: ReactNode;
}

export default function TopicSearchAutocomplete({
  variant,
  idPrefix,
  topicTree = [],
  selectedTopicIds = [],
  onToggleTopic,
  onAddTopicFromSearch,
  loading = false,
  error = null,
  loadingMessage = '',
  placeholder,
  inputAriaLabel,
  listAriaLabel,
  selectedBadgeText,
  getRemoveChipAriaLabel,
  trailingSlot = null
}: TopicSearchAutocompleteProps) {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.contentEditor;
  const fieldRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);

  const safeTree = useMemo(() => (Array.isArray(topicTree) ? topicTree : []), [topicTree]);
  const flattenedRows = useMemo(() => flattenTopicTreeForAdmin(safeTree) ?? [], [safeTree]);

  const rowsById = useMemo(() => {
    const map = new Map();
    flattenedRows.forEach(row => {
      if (row?.id != null) map.set(String(row.id), row);
    });
    return map;
  }, [flattenedRows]);

  const topicParentByChildId = useMemo(() => buildTopicParentMap(safeTree), [safeTree]);
  const topicNodeById = useMemo(() => buildTopicNodeMap(safeTree), [safeTree]);

  const displayedTopicIds = useMemo(
    () => filterTopicIdsForChipRow(selectedTopicIds, topicParentByChildId, topicNodeById),
    [selectedTopicIds, topicParentByChildId, topicNodeById]
  );

  const queryNormalized = inputValue.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!queryNormalized) return [];
    return flattenedRows
      .filter(row => {
        const display = String(row?.displayName ?? row?.name ?? '').toLowerCase();
        const name = String(row?.name ?? '').toLowerCase();
        return (
          (display && display.includes(queryNormalized)) ||
          (name && name.includes(queryNormalized))
        );
      })
      .slice(0, 10);
  }, [flattenedRows, queryNormalized]);

  const showList =
    isListOpen && queryNormalized && (loading || matches.length > 0);

  const listboxId = `${idPrefix}-suggestions`;

  const isSelected = id => (selectedTopicIds || []).some(tid => String(tid) === String(id));

  const addTopicIfNew = row => {
    if (row?.id === undefined || row?.id === null) return;
    if (isSelected(row.id)) return;
    if (typeof onAddTopicFromSearch === 'function') {
      onAddTopicFromSearch(row.id);
    } else {
      onToggleTopic(row.id);
    }
    setInputValue('');
    setIsListOpen(false);
  };

  const handleInputChange = event => {
    const next = event.target.value;
    setInputValue(next);
    setIsListOpen(Boolean(next.trim()));
  };

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      setIsListOpen(false);
      return;
    }
    if (event.key !== 'Enter' && event.key !== 'NumpadEnter') return;
    event.preventDefault();
    if (!queryNormalized) return;
    const exact = flattenedRows.find(row => {
      if (isSelected(row.id)) return false;
      const n = String(row?.name ?? '')
        .trim()
        .toLowerCase();
      const d = String(row?.displayName ?? row?.name ?? '')
        .trim()
        .toLowerCase();
      return n === queryNormalized || d === queryNormalized;
    });
    const firstSelectable = matches.find(row => !isSelected(row.id));
    const pick = exact || firstSelectable;
    if (pick) addTopicIfNew(pick);
  };

  useEffect(() => {
    if (!isListOpen) return;
    const onDocMouseDown = event => {
      const el = fieldRef.current;
      if (!el) return;
      if (el.contains(event.target)) return;
      setIsListOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isListOpen]);

  const fieldInner = (
    <div
      className={['keyword-search__field', styles.fieldExtra].filter(Boolean).join(' ')}
      ref={fieldRef}>
      <input
        type="search"
        className="search-page__query-input"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={inputAriaLabel}
        aria-autocomplete="list"
        aria-expanded={Boolean(showList)}
        aria-controls={listboxId}
        autoComplete="off"
      />
      {showList ? (
        <ul id={listboxId} className={styles.list} role="listbox" aria-label={listAriaLabel}>
          {loading && matches.length === 0 ? (
            <li className={[styles.item, styles.itemLoading].filter(Boolean).join(' ')} role="presentation">
              <span
                className={styles.loadingText || 'topic-search-autocomplete__loading-text'}
                aria-live="polite">
                {loadingMessage}
              </span>
            </li>
          ) : null}
          {matches.map(row => {
            const selected = isSelected(row.id);
            const label = row?.displayName || row?.name || String(row.id);
            return (
              <li key={row?.key ?? String(row.id)} className={styles.item} role="none">
                <button
                  type="button"
                  className={`${styles.button}${selected ? ` ${styles.buttonSelected}` : ''}`}
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    if (!selected) {
                      addTopicIfNew(row);
                    } else {
                      setIsListOpen(false);
                    }
                    setInputValue('');
                  }}>
                  <span className={styles.label}>{label}</span>
                  {selected ? <span className={styles.meta}>{selectedBadgeText}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );

  const searchBlock =
    variant === 'newsletter' ? (
      <div className="keyword-search">
        <div className="personal-data__newsletter-description">{fieldInner}</div>
      </div>
    ) : (
      <div className={`keyword-search ${styles.outer || ''}`.trim()}>{fieldInner}</div>
    );

  return (
    <>
      {searchBlock}
      {error ? (
        <p
          className={
            variant === 'contentEditor' ? 'content-editor__topic-tree-error' : 'topic-search-autocomplete__error'
          }
          role="alert">
          {String(error)}
        </p>
      ) : null}
      {trailingSlot}
      {displayedTopicIds.length > 0 ? (
        <div className="keyword-search__keywords">
          {displayedTopicIds.map(id => {
            const row = rowsById.get(String(id));
            const label = row ? row.displayName || row.name : String(id);
            return (
              <span key={String(id)} className="keyword-search__keyword" aria-label={label}>
                <span>{label}</span>
                <button
                  type="button"
                  className={styles.chipRemoveClass}
                  onClick={() => onToggleTopic(id)}
                  aria-label={getRemoveChipAriaLabel(label)}>
                  {variant === 'contentEditor' ? <CloseIcon width={12} height={12} /> : <CrossIcon />}
                </button>
              </span>
            );
          })}
        </div>
      ) : null}
    </>
  );
}
